<script lang="ts" setup>
import { ButtonComponent } from '@metapic-apps/shared'
import { ElNotification, type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'

import { useUpdateCampaignParticipant } from '@/api/campaignParticipant.api'
import RefreshIcon from '@/assets/icons/refresh.svg?component'
import { formatMoney } from '@/lib/helpers'
import type { CurrencyType } from '@/lib/types'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  campaignId: number
  participantId: number | string
  showResetToDefaultButton: boolean
  isDisabled: boolean
  currency: CurrencyType
  participantFixedFee?: number | null
  defaultCampaignFixedFee?: number
}>()

const emits = defineEmits(['onChange'])

const { addFixedFee, removeFixedFee, isLoading: isRemoveFixedFeeLoading } = useUpdateCampaignParticipant(props.campaignId)
const isDirty = ref(false)
const formRef = ref<FormInstance>()
const formData = reactive({
  fixedFee: props.participantFixedFee || props.defaultCampaignFixedFee || 0,
})

const shouldShowResetDialog = ref(false)

const rules = reactive<FormRules<{ fixedFee: string }>>({
  fixedFee: [
    {
      validator: (_, value: string, callback: (e?: Error) => void) => {
        if (!value) {
          return callback(new Error('Field must be a valid number'))
        }

        const formattedValue = Number(value)
        if (isNaN(formattedValue)) {
          return callback(new Error('Field must be a valid number'))
        }
        if (formattedValue < 0) {
          return callback(new Error('Field must be a positive number'))
        }
        if (formattedValue > 1000000) {
          return callback(new Error('Field must be less than 1000000'))
        }

        return callback()
      },
    },
  ],
})
const handleSubmit = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate(async (valid) => {
    if (valid) {
      isDirty.value = false
      const hasTheSameFeeAsCampaignFee = Number(props.defaultCampaignFixedFee) === Number(formData.fixedFee)
      if (hasTheSameFeeAsCampaignFee) {
        await removeFixedFee(props.participantId)
      } else {
        await addFixedFee(props.participantId, { amount: formData.fixedFee, currency: props.currency.code })
      }
      emits('onChange', Number(formData.fixedFee))
      ElNotification.success('Fixed fee was successfully changed.')
    }
  })
}
const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }
  formEl.resetFields(['fixedFee'])
  isDirty.value = false
}

const resetFixedFee = async () => {
  await removeFixedFee(props.participantId)
  shouldShowResetDialog.value = false
  emits('onChange', props.defaultCampaignFixedFee)
  if (props.defaultCampaignFixedFee) {
    formData.fixedFee = props.defaultCampaignFixedFee
  }
  ElNotification.success('Participant fee was successfully changed to the default campaign fee.')
  isDirty.value = false
}

const formattedCampaignFixedFee =
  props.defaultCampaignFixedFee && props.currency ? formatMoney(props.defaultCampaignFixedFee, props.currency) : null
</script>
<template>
  <el-form ref="formRef" :model="formData" :rules="rules" @submit.prevent="handleSubmit(formRef)">
    <BoxComponent :mb="16">
      <TypographyComponent variant="headline-small">Fixed fee </TypographyComponent>
    </BoxComponent>
    <BoxComponent>
      <el-form-item prop="fixedFee">
        <el-input v-model="formData.fixedFee" size="large" :disabled="isDisabled" @keyup="isDirty = true">
          <template #suffix>
            <TypographyComponent variant="title-large" style="margin-bottom: 0"> {{ currency?.symbol }} </TypographyComponent>
          </template>
        </el-input>
      </el-form-item>
    </BoxComponent>
    <FlexComponent justify="space-between">
      <FlexComponent v-if="isDirty" :gap="8">
        <ButtonComponent variant="primary" type="submit">Save</ButtonComponent>
        <ButtonComponent variant="text" @click="resetForm(formRef)">Cancel</ButtonComponent>
      </FlexComponent>
      <BoxComponent ml="auto">
        <ButtonComponent
          v-if="showResetToDefaultButton && Number(formData.fixedFee) !== defaultCampaignFixedFee"
          variant="text"
          @click="
            () => {
              shouldShowResetDialog = true
            }
          "
        >
          <FlexComponent align="center" :gap="8">
            <el-icon :size="18">
              <RefreshIcon />
            </el-icon>
            Reset to default
          </FlexComponent>
        </ButtonComponent>
      </BoxComponent>
    </FlexComponent>
    <el-dialog
      v-if="formattedCampaignFixedFee"
      :model-value="shouldShowResetDialog"
      width="480px"
      @close="() => (shouldShowResetDialog = false)"
    >
      <template #header>
        <TypographyComponent variant="headline-medium"> Reset to default</TypographyComponent>
      </template>
      <TypographyComponent variant="body-large">
        Are you sure you want to reset the fixed fee to the campaign's default value of {{ formattedCampaignFixedFee }}?
      </TypographyComponent>
      <template #footer>
        <FlexComponent :gap="8" justify="flex-end">
          <ButtonComponent variant="text" @click="() => (shouldShowResetDialog = false)"> Cancel </ButtonComponent>
          <ButtonComponent variant="accent" :is-loading="isRemoveFixedFeeLoading" @click="resetFixedFee">Reset</ButtonComponent>
        </FlexComponent>
      </template>
    </el-dialog>
  </el-form>
</template>
